import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = unauth, object = auth
  const [theme, setThemeState] = useState(localStorage.getItem("ht_theme") || "light");

  const applyTheme = useCallback((t) => {
    const root = document.documentElement;
    if (t === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    localStorage.setItem("ht_theme", t);
  }, []);

  useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);

  useEffect(() => {
    const token = localStorage.getItem("ht_token");
    if (!token) {
      setUser(false);
      return;
    }
    api.get("/auth/me")
      .then(({ data }) => {
        setUser(data);
        if (data.theme) setThemeState(data.theme);
      })
      .catch(() => {
        localStorage.removeItem("ht_token");
        setUser(false);
      });
  }, []);

  const login = async (email) => {
    try {
      const { data } = await api.post("/auth/login", { email });
      localStorage.setItem("ht_token", data.token);
      setUser(data.user);
      if (data.user.theme) setThemeState(data.user.theme);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const demoLogin = async () => {
    try {
      const { data } = await api.post("/auth/demo");
      localStorage.setItem("ht_token", data.token);
      setUser(data.user);
      if (data.user.theme) setThemeState(data.user.theme);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem("ht_token");
    setUser(false);
  };

  const toggleTheme = async () => {
    const next = theme === "dark" ? "light" : "dark";
    setThemeState(next);
    try { await api.patch("/auth/theme", { theme: next }); } catch (e) { /* ignore */ }
  };

  return (
    <AuthContext.Provider value={{ user, login, demoLogin, logout, theme, toggleTheme }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
